// src/tabs/calendar/_min-stay-helpers.js — Phase 6.B PR #167c (ADR-101 D-8)
//
// Helpers purs pour le rendu du label min_stay dans les cellules calendrier
// (TableauView ; MensuelView retirée PR #169d — formatMinStayCellLabel
//  encore exporté pour rétro-compat tests + ré-usage potentiel vue Liste PR #169c).
// Différencie 3 états visuels :
//
//   1. Override Niveau 3 ≠ défaut Niveau 1  → amber + badge ⚙️
//   2. Override Niveau 3 = défaut Niveau 1  → amber simple
//      (override existant mais "redondant" — pas de badge spécial)
//   3. Pas d'override + défaut Niveau 1     → gris pâle #888
//   4. Ni l'un ni l'autre                   → null (rien affiché)
//
// 2 helpers exposés :
//   - `formatMinStayCellLabel({override, defaultValue, showMinStay})` →
//     retourne `{text: 'min Nn', color, badge}` (format historique avec
//     préfixe `min` et suffixe `n`, ex-MensuelView — gardé pour rétro-compat).
//   - `formatMinStayCellShort({override, defaultValue, showMinStay})` →
//     retourne `{text: '1' | '2' | ..., color, badge}` (chiffre seul,
//     utilisé par TableauView depuis amend C18 — col 1 sticky porte le
//     titre "Nb nuits min", les cellules n'ont besoin que du chiffre).
//
// Normalisation BPRcv2-2 (pessimist mini v1.2) :
//   - `override === 0` traité comme null (cohérent garde backend S-3 :
//     Beds24 stocke 0 = retrait, FBZ traduit 0 → NULL).
//   - `override === undefined` traité comme null (Map.get peut retourner
//     undefined si la clé n'existe pas).
//   - `==` catch null+undefined symétriquement (intentionnel).
//
// Pas de side-effect : appelables inline OU mémoïsés.

/**
 * Calcule le label visuel min_stay pour une cellule calendrier.
 *
 * @param {object} args
 * @param {number|null|undefined} args.override
 *   Valeur Niveau 3 (daily_pricing.min_stay) pour cette date+logement.
 *   0/null/undefined → normalisé `null` (BPRcv2-2).
 * @param {number|null|undefined} args.defaultValue
 *   Valeur Niveau 1 (logement_pricing_config.min_stay_default) pour le logement.
 *   0/null/undefined → normalisé `null` (BPRcv2-2).
 * @param {boolean} args.showMinStay
 *   Toggle calendrier 📐 Min nuits (Phase 6 PR #167b). Si false → return null.
 *
 * @returns {{text:string, color:string, badge:string|null}|null}
 *   - `null` si rien à afficher (showMinStay=false OU normalized null/null).
 *   - Sinon : { text: 'min Nn', color: '#92400e'|'#888', badge: '⚙️'|null }.
 */
export function formatMinStayCellLabel({ override, defaultValue, showMinStay }) {
  const shortLabel = formatMinStayCellShort({ override, defaultValue, showMinStay });
  if (!shortLabel) return null;
  // Format historique : `min Nn` (ex-MensuelView, gardé pour rétro-compat).
  return {
    text: `min ${shortLabel.text}n`,
    color: shortLabel.color,
    badge: shortLabel.badge,
  };
}

/**
 * PR #167c amend C18 — variante "chiffre seul" pour TableauView.
 *
 * Retourne le même `{color, badge}` que `formatMinStayCellLabel` mais avec
 * `text` = chiffre brut (`'1'`, `'2'`, ...) sans préfixe `min` ni suffixe `n`.
 * La col 1 sticky de TableauView porte désormais le titre "Nb nuits min",
 * donc les cellules n'ont besoin que du chiffre pour rester lisibles à 24px.
 *
 * Mêmes règles de couleur / badge que le helper historique :
 *   1. Override ≠ défaut → amber `#92400e` + badge ⚙️
 *   2. Override = défaut → amber simple (no badge)
 *   3. Pas d'override + défaut défini → gris pâle `#888`
 *   4. Ni l'un ni l'autre → null
 *
 * @param {object} args
 * @param {number|null|undefined} args.override
 * @param {number|null|undefined} args.defaultValue
 * @param {boolean} args.showMinStay
 * @returns {{text:string, color:string, badge:string|null}|null}
 */
export function formatMinStayCellShort({ override, defaultValue, showMinStay }) {
  if (!showMinStay) return null;

  // Normalisation tri-valeurs ADR-101 D-2 + BPRcv2-2 :
  // 0 traité comme null (garde sémantique S-3 backend).
  // undefined traité comme null (Map.get peut retourner undefined).
  // `==` catch null + undefined symétriquement.
  const o = override == null || override === 0 ? null : override;
  const d = defaultValue == null || defaultValue === 0 ? null : defaultValue;

  // État 4 : ni override Niveau 3 ni défaut Niveau 1 → rien affiché
  if (o === null && d === null) return null;

  // État 3 : pas d'override mais défaut défini → gris pâle (hérité)
  if (o === null && d !== null) {
    return { text: String(d), color: '#888', badge: null };
  }

  // États 1/2 : override actif (o !== null)
  if (o !== null && o !== d) {
    // État 1 : override ≠ défaut → amber + badge ⚙️
    return { text: String(o), color: '#92400e', badge: '⚙️' };
  }

  // État 2 : override === défaut → amber simple (override existant mais "redondant")
  // Couvre aussi le cas o !== null && d === null (override sans défaut configuré)
  // → considéré "actif" avec badge ⚙️ (cf. plan §5.2 ligne `5 / null`).
  if (o !== null && d === null) {
    return { text: String(o), color: '#92400e', badge: '⚙️' };
  }
  // o !== null && o === d
  return { text: String(o), color: '#92400e', badge: null };
}
